import { useState, useMemo, useEffect } from "react";
import { Plus, Trash2, Split as SplitIcon, AlertCircle } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { useFormatCurrency } from "@/lib/CurrencyContext";
import {
  ACTUAL_CATEGORIES, newActualId,
} from "@/lib/actuals";
import ActualAllocationPicker from "@/components/ActualAllocationPicker";

/**
 * Split-receipt dialog.
 *
 * Lets the user divide a single logged actual entry across multiple line
 * items — e.g. one R5,000 hardware-store receipt becomes "R3,500 → Master
 * Bath · Tiling" + "R1,500 → Kitchen · Plumbing". Each split row owns its
 * own category, allocation, amount and optional note. All splits inherit
 * the original entry's date.
 *
 * Behaviour:
 *  - On Apply, the original entry is removed and replaced with the new
 *    splits. If the splits don't sum exactly to the original, an unallocated
 *    "Remainder" entry is appended so no money is lost.
 *  - "purchase" + "other" categories don't show an allocation picker
 *    (matches ActualsLog quick-add UX).
 *
 * Props:
 *  - open: bool
 *  - onOpenChange(open)
 *  - entry: the actual being split — {id, date, category, amount, note, ref_id}
 *  - project: full project (for the allocation pickers)
 *  - onApply(replacementEntries: array): caller swaps `entry` for the array
 */
export default function SplitReceiptDialog({
  open,
  onOpenChange,
  entry,
  project,
  onApply,
}) {
  const fmt = useFormatCurrency();
  const original = Number(entry?.amount) || 0;

  // Seed the split rows whenever the dialog re-opens for a new entry.
  // Default to two rows split 50/50 — the most-common "I forgot to allocate
  // this earlier" workflow. The user can drop/edit either side immediately.
  const [splits, setSplits] = useState([]);
  useEffect(() => {
    if (!open || !entry) return;
    const half = Math.round((original / 2) * 100) / 100;
    const otherHalf = Math.round((original - half) * 100) / 100;
    setSplits([
      {
        _row: newActualId(),
        category: entry.category || "interior",
        ref_id: entry.ref_id || "",
        amount: half,
        note: entry.note || "",
      },
      {
        _row: newActualId(),
        category: entry.category || "interior",
        ref_id: "",
        amount: otherHalf,
        note: "",
      },
    ]);
  }, [open, entry, original]);

  const allocated = useMemo(
    () => splits.reduce((s, r) => s + (Number(r.amount) || 0), 0),
    [splits]
  );
  const remainder = useMemo(
    () => Math.round((original - allocated) * 100) / 100,
    [original, allocated]
  );

  const updateRow = (rowId, patch) => {
    setSplits((rows) =>
      rows.map((r) => (r._row === rowId ? { ...r, ...patch } : r))
    );
  };

  const removeRow = (rowId) => {
    setSplits((rows) => rows.filter((r) => r._row !== rowId));
  };

  const addRow = () => {
    setSplits((rows) => [
      ...rows,
      {
        _row: newActualId(),
        category: entry?.category || "interior",
        ref_id: "",
        amount: Math.max(0, remainder),
        note: "",
      },
    ]);
  };

  const splitEvenly = () => {
    if (splits.length === 0) return;
    const each = Math.floor((original / splits.length) * 100) / 100;
    const last = Math.round((original - each * (splits.length - 1)) * 100) / 100;
    setSplits((rows) =>
      rows.map((r, i) => ({
        ...r,
        amount: i === rows.length - 1 ? last : each,
      }))
    );
  };

  const canApply =
    splits.length >= 2 &&
    splits.every((r) => Number(r.amount) > 0 && r.category);

  const handleApply = () => {
    if (!entry || !canApply) return;
    const date = entry.date;
    const out = splits.map((r) => ({
      id: newActualId(),
      date,
      category: r.category,
      amount: Number(r.amount) || 0,
      note: r.note || "",
      ref_id: r.ref_id || "",
    }));
    // Preserve the receipt total even if the user under-allocated. We tack
    // an unallocated "Remainder" entry onto the original category so the
    // category-level total still reconciles to the receipt.
    if (Math.abs(remainder) > 0.01) {
      out.push({
        id: newActualId(),
        date,
        category: entry.category || "other",
        amount: remainder,
        note: entry.note ? `${entry.note} — remainder` : "Remainder",
        ref_id: "",
      });
    }
    onApply(out);
    onOpenChange(false);
  };

  if (!entry) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-2xl bg-white"
        data-testid="split-receipt-dialog"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 fc-heading text-lg text-[#1C1C1C]">
            <SplitIcon className="w-4 h-4" />
            Split this receipt
          </DialogTitle>
          <div className="text-xs text-[#78716C]">
            Divide{" "}
            <span className="fc-mono text-[#1C1C1C]">{fmt(original)}</span> from{" "}
            <span className="fc-mono">{entry.date}</span> across multiple line
            items. The original entry will be replaced.
          </div>
        </DialogHeader>

        {/* Split rows */}
        <div className="space-y-3 max-h-[55vh] overflow-y-auto pr-1">
          {splits.map((r, idx) => (
            <div
              key={r._row}
              className="p-3 rounded-lg border border-slate-brand-200 bg-slate-brand-50/40 space-y-2"
              data-testid={`split-row-${idx}`}
            >
              <div className="flex items-center justify-between">
                <div className="text-[10px] uppercase tracking-[0.15em] text-[#A8A29E]">
                  Split {idx + 1}
                </div>
                {splits.length > 1 && (
                  <button
                    type="button"
                    onClick={() => removeRow(r._row)}
                    data-testid={`split-remove-${idx}`}
                    className="text-[#A8A29E] hover:text-[#DC2626] transition-colors"
                    aria-label="Remove split"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-[160px_1fr_120px] gap-2">
                <Select
                  value={r.category}
                  onValueChange={(v) =>
                    updateRow(r._row, { category: v, ref_id: "" })
                  }
                >
                  <SelectTrigger
                    className="fc-input h-9 rounded-md text-sm"
                    data-testid={`split-category-${idx}`}
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {ACTUAL_CATEGORIES.map((c) => (
                      <SelectItem key={c.value} value={c.value}>
                        {c.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Input
                  value={r.note}
                  onChange={(e) => updateRow(r._row, { note: e.target.value })}
                  placeholder="Note (e.g. tiling deposit)"
                  className="fc-input h-9 rounded-md text-sm"
                  data-testid={`split-note-${idx}`}
                />
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  value={r.amount}
                  onChange={(e) =>
                    updateRow(r._row, { amount: e.target.value })
                  }
                  className="fc-input h-9 rounded-md text-sm text-right fc-mono"
                  data-testid={`split-amount-${idx}`}
                />
              </div>

              {/* Allocation picker — purchase/other don't have line items. */}
              {r.category !== "purchase" && r.category !== "other" && (
                <div className="pt-1">
                  <ActualAllocationPicker
                    category={r.category}
                    project={project}
                    refId={r.ref_id}
                    onChange={(rid) => updateRow(r._row, { ref_id: rid })}
                    testPrefix={`split-alloc-${idx}`}
                  />
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Toolbar */}
        <div className="flex flex-wrap items-center justify-between gap-2 pt-2 border-t border-black/[0.06]">
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={addRow}
              data-testid="split-add-row"
              className="text-[11px] text-slate-brand-700 hover:text-slate-brand-500 inline-flex items-center gap-1 border border-slate-brand-200 bg-slate-brand-50 rounded-full h-7 px-2.5 transition-colors"
            >
              <Plus className="w-3 h-3" /> Add split
            </button>
            <button
              type="button"
              onClick={splitEvenly}
              data-testid="split-even"
              className="text-[11px] text-[#57534E] hover:text-[#1C1C1C] inline-flex items-center gap-1 border border-black/[0.08] hover:border-black/[0.2] rounded-full h-7 px-2.5 transition-colors"
            >
              Split evenly
            </button>
          </div>
          <div
            className="text-[11px] text-right"
            data-testid="split-remainder-status"
          >
            <div className="text-[#78716C]">
              Allocated{" "}
              <span className="fc-mono text-[#1C1C1C]">{fmt(allocated)}</span>{" "}
              of <span className="fc-mono">{fmt(original)}</span>
            </div>
            <div
              className={`mt-0.5 inline-flex items-center gap-1 ${
                Math.abs(remainder) < 0.01
                  ? "text-slate-brand-700"
                  : remainder > 0
                  ? "text-[#B45309]"
                  : "text-[#DC2626]"
              }`}
            >
              {Math.abs(remainder) >= 0.01 && (
                <AlertCircle className="w-3 h-3" />
              )}
              {Math.abs(remainder) < 0.01
                ? "Balanced"
                : remainder > 0
                ? `${fmt(remainder)} unallocated (kept as remainder)`
                : `Over by ${fmt(-remainder)}`}
            </div>
          </div>
        </div>

        <DialogFooter className="gap-2 sm:gap-2">
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            data-testid="split-cancel"
            className="text-sm border border-black/[0.08] hover:border-black/[0.2] rounded-md h-9 px-3 inline-flex items-center"
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={!canApply}
            onClick={handleApply}
            data-testid="split-apply"
            className="bg-[#1C1C1C] text-white hover:bg-[#2A2A2E] disabled:opacity-50 disabled:cursor-not-allowed rounded-md h-9 px-4 text-sm font-medium inline-flex items-center gap-1.5 transition-colors"
          >
            Apply {splits.length}-way split
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
